import Markdown from 'shared/components/Markdown';
import { BORDER_RADIUSES, COLORS, FONT_WEIGHTS, SPACINGS } from 'shared/styles/variables';
import styled from 'styled-components';

import Spacer from 'design-system/components/Spacer';
import IconFile from 'images/icons/file.svg';
import Button, { ButtonType } from 'inquiry/components/Button';
import CustomAssetWithFallback from 'inquiry/components/CustomAssetWithFallback';
import InlineLoadingIndicator from 'inquiry/components/InlineLoadingIndicator';
import ThemedParagraph from 'inquiry/components/ThemedParagraph';
import ThemedTitle from 'inquiry/components/ThemedTitle';
import { SerializedButtonStyles } from 'lib/interfaces/StepsConfig';
import type {
  ImageRemoteComponent,
  TextComponentStyles,
  TitleComponentStyles,
} from 'lib/interfaces/ui-components';
import PositionableButtonContainer from 'verify/components/PositionableButtonContainer/PositionableButtonContainer';
import ThemedSvg from 'verify/components/ThemedSvg';
import { MIN_HEIGHTS } from 'verify/styles/variables';

const Container = styled.div`
  display: flex;
  flex-direction: column;
`;

const Header = styled('div')`
  min-height: ${MIN_HEIGHTS.header};
  margin-bottom: ${SPACINGS.base * 4}px;
`;

const ConfirmFileContainer = styled('div')`
  background-color: ${COLORS.gray4};
  border-radius: ${BORDER_RADIUSES.rounded};
  margin-bottom: ${SPACINGS.base * 8}px;
  min-height: 80px;
  padding: 0px ${SPACINGS.base * 2}px;

  display: flex;
  flex-direction: row;
  justify-content: start;
  align-items: center;
`;

const IconContainer = styled.div`
  margin-right: ${SPACINGS.base}px;
  display: flex;
`;

const FileNameContainer = styled('div')`
  color: ${COLORS.black};
  font-weight: ${FONT_WEIGHTS.semiBold};
  word-break: break-all;
`;

const Minify = styled('div')`
  display: inline;

  img {
    height: 12px;
    margin-bottom: 4px;
  }
`;

interface ConfirmFileUploadViewProps {
  customFileIcon?: ImageRemoteComponent;
  fileName: string;
  isProcessing: boolean;
  handleContinue: () => void;
  handleRetry: () => void;
  titleStyles?: TitleComponentStyles;
  textStyles?: TextComponentStyles;
  buttonStyles?: SerializedButtonStyles;
  // Localizations
  title: string;
  prompt: string;
  uploading: string;
  btnSubmit: string;
  btnRetake: string;
}

export default function ConfirmFileUploadView({
  customFileIcon,
  fileName,
  isProcessing,
  handleContinue,
  handleRetry,
  titleStyles,
  textStyles,
  buttonStyles,
  // Localizations
  title,
  prompt,
  uploading,
  btnSubmit,
  btnRetake,
}: ConfirmFileUploadViewProps) {
  return (
    <Container>
      <Header>
        <ThemedTitle styles={titleStyles}>
          <Markdown>{title}</Markdown>
        </ThemedTitle>
        <ThemedParagraph styles={textStyles}>
          <Markdown>{prompt}</Markdown>
        </ThemedParagraph>
      </Header>

      <ConfirmFileContainer>
        <IconContainer>
          <CustomAssetWithFallback customImage={customFileIcon} alt='file icon'>
            <ThemedSvg>
              <IconFile />
            </ThemedSvg>
          </CustomAssetWithFallback>
        </IconContainer>
        <FileNameContainer>{fileName}</FileNameContainer>
      </ConfirmFileContainer>

      <PositionableButtonContainer>
        <Button
          buttonType={ButtonType.Primary}
          styles={buttonStyles?.[ButtonType.Primary]}
          disabled={isProcessing}
          onClick={handleContinue}
          data-test='confirm-file-upload__continue'
        >
          {isProcessing ? (
            <span>
              {uploading}
              <Minify>
                <InlineLoadingIndicator />
              </Minify>
            </span>
          ) : (
            btnSubmit
          )}
        </Button>

        {/* Assume spacing is specified by styles if present */}
        {buttonStyles == null && <Spacer height={2} />}

        <Button
          buttonType={ButtonType.Secondary}
          styles={buttonStyles?.[ButtonType.Secondary]}
          onClick={handleRetry}
          disabled={isProcessing}
          data-test='confirm-file-upload__retry'
        >
          {btnRetake}
        </Button>
      </PositionableButtonContainer>
    </Container>
  );
}
